/**
 * Client de l'API de recherche — L3.
 *
 * Module distinct de `apiCatalogue.ts` pour la même raison qui l'avait séparé de `api.ts` : les
 * trois surfaces n'ont ni le même rythme de changement ni les mêmes règles.
 */

import { appeler } from './api'
import type { Criteres, Frequence, Tri } from './criteres'
import { ecrireCriteres } from './criteres'

export interface Vignette {
  id: string
  title: string
  district: string
  /** Libellé FRANÇAIS, conservé par compatibilité : l'interface lit `cityCode` et le référentiel. */
  city: string
  /** Code du référentiel — `SALA_AL_JADIDA` (D44). C'est lui qui circule. */
  cityCode: string
  latitude: number
  longitude: number
  rentAllInclusive: number
  currencyCode: string
  unitKind: 'EntireProperty' | 'PrivateRoom' | 'SharedRoom'
  bedroomCount: number
  isFurnished: boolean
  availableFrom: string
  hasOriginalPhotos: boolean
  isVerifiedOnSite: boolean
  coverMediaId: string | null
  /**
   * Combien de photos porte l'annonce, couverture comprise — ticket `fo-annonce-11`.
   *
   * Un compte, et non la liste des médias : la décision du 07/09/2026 d'écarter le carrousel par
   * carte tient, et un entier suffit à écrire « 14 » sur la couverture.
   */
  photoCount: number
  publishedAt: string | null
  /**
   * Encore sur le marché ? Toujours vrai en recherche, qui ne rend que des annonces publiées.
   *
   * Ne porte réellement une information que sur les FAVORIS : une annonce gardée puis louée ou
   * retirée y reste affichée, grisée, jusqu'à ce qu'on l'enlève (ticket `fo-favoris-01`). La faire
   * disparaître serait pire — on chercherait ce qu'on a perdu.
   */
  isAvailable: boolean
  isFavorite: boolean
}

export interface PageResultats {
  items: Vignette[]
  page: number
  size: number
  total: number
}

export interface Punaise {
  id: string
  latitude: number
  longitude: number
  rentAllInclusive: number
  coverMediaId: string | null
}

export interface Punaises {
  pins: Punaise[]
  /** Le serveur a coupé : l'interface doit le dire, jamais l'avaler (§1.4). */
  truncated: boolean
  limit: number
  /** La ville réellement interrogée — voir `PageCurseur`. */
  appliedCityCode: string
}

/** Où en est une ville (D44) : en amorçage, on y publie et on y cherche, mais elle n'entre pas dans le sélecteur. */
export type StatutVille = 'Seeding' | 'Open'

/**
 * Une ville du référentiel — `GET /v1/cities`.
 *
 * **Le centre et le rayon sont publics, et c'est tout le dispositif de D45** : la liste entière
 * arrive au client, qui calcule LUI-MÊME la ville la plus proche de sa position. Aucune coordonnée
 * d'utilisateur ne part vers le serveur — il n'existe aucune route pour en recevoir une.
 *
 * Les trois libellés plutôt qu'un seul : la réponse est en cache du service worker (invariant
 * 17), et une réponse qui dépendrait de la langue cesserait d'être cachable.
 */
export interface Ville {
  code: string
  labelFr: string
  labelAr: string
  labelEn: string
  regionCode: string
  metroCode: string | null
  latitude: number
  longitude: number
  radiusMetres: number
  defaultZoom: number
  status: StatutVille
  /** Annonces publiées : ce que la feuille de choix affiche à côté du nom. */
  publishedListingCount: number
}

export interface CatalogueVilles {
  /** Ce que le serveur applique faute de mieux — évite un aller-retour pour l'apprendre. */
  defaultCityCode: string
  items: Ville[]
}

/**
 * Ce que l'adresse IP a pu dire — `GET /v1/cities/nearby`, **une proposition, jamais un choix**.
 *
 * `source` vaut `ip` quand une ville a été trouvée, `none` sinon : base absente (elle se livre
 * désactivée, D45), adresse muette, ou point hors de tout rayon. Jamais en cache : la réponse
 * dépend de l'appelant.
 */
export interface IndiceVille {
  cityCode: string | null
  source: 'ip' | 'none'
}

export interface Quartier {
  /** Forme normalisée : c'est elle qui part dans les filtres. */
  value: string
  /** Orthographe majoritaire : c'est elle qu'on affiche. */
  label: string
  listingCount: number
}

export interface Ancre {
  id: string
  code: string
  kind: 'University' | 'BusinessPark' | 'TransitStation' | 'Other'
  labelFr: string
  labelAr: string
  labelEn: string
  latitude: number
  longitude: number
}

/**
 * Ce que l'écran de recherche propose de cocher d'un geste — `GET /v1/search/options`.
 *
 * Les paliers de la puce « jusqu'à X » sont un RÉGLAGE du back-office, plus une constante du
 * front : le site et l'application lisent la même réponse, ils ne peuvent plus diverger (constat
 * du 10/09/2026 : 2 000 / 3 000 / 4 000 / 6 000 d'un côté, 2 000 / 3 000 / 4 500 de l'autre).
 * Public et non personnalisé : en liste blanche du service worker, comme les quartiers.
 */
export interface OptionsRecherche {
  budgetCaps: number[]
  currencyCode: string
}

export interface RechercheEnregistree {
  id: string
  name: string
  criteria: Criteres
  frequency: Frequence
  isActive: boolean
  createdAt: string
  lastRunAt: string | null
  lastSeenAt: string | null
  /** Le compteur de nouveautés — c'est l'alerte tant que L1c est bloqué (§1.5). */
  unseenCount: number
}

export function rechercher(criteres: Criteres, tri: Tri, page: number, taille = 20): Promise<PageResultats> {
  const params = ecrireCriteres(criteres, {
    sort: tri,
    page: String(page),
    size: String(taille),
  })

  return appeler<PageResultats>(`/search?${params}`)
}

export function rechercherPunaises(criteres: Criteres): Promise<Punaises> {
  return appeler<Punaises>(`/search/map?${ecrireCriteres(criteres)}`)
}

/**
 * Les référentiels sont PAR VILLE depuis L13 : quartiers, ancres et paliers de budget d'une ville
 * ne disent rien d'une autre. Sans code, le serveur rend ceux de la ville par défaut.
 */
function parVille(code?: string): string {
  return code === undefined || code === '' ? '' : `?city=${encodeURIComponent(code)}`
}

export function lireOptionsRecherche(ville?: string): Promise<OptionsRecherche> {
  return appeler<OptionsRecherche>(`/search/options${parVille(ville)}`)
}

export function listerQuartiers(ville?: string): Promise<Quartier[]> {
  return appeler<Quartier[]>(`/districts${parVille(ville)}`)
}

/** Le référentiel des villes. Public, non personnalisé : en liste blanche du service worker. */
export function listerVilles(): Promise<CatalogueVilles> {
  return appeler<CatalogueVilles>('/cities')
}

/**
 * La ville que l'adresse IP suggère. Avant-dernier maillon de la chaîne (D45, §2.9) : souvent
 * `none`, et toujours une proposition. L'échec réseau vaut `none` — ce maillon ne bloque rien.
 */
export function villeProche(): Promise<IndiceVille> {
  return appeler<IndiceVille>('/cities/nearby').catch(() => ({ cityCode: null, source: 'none' }))
}

/** Autocomplétion du champ « Quartier » de l'assistant de publication (§1.1). */
export function suggererQuartiers(prefixe: string): Promise<string[]> {
  return appeler<string[]>(`/districts/suggest?q=${encodeURIComponent(prefixe)}`)
}

export function listerAncres(ville?: string): Promise<Ancre[]> {
  return appeler<Ancre[]>(`/anchors${parVille(ville)}`)
}

/**
 * Garde une annonce. `PUT` : l'acte est idempotent, un double appui ne produit pas d'erreur.
 */
export function ajouterFavori(annonceId: string): Promise<void> {
  return appeler<void>(`/favorites/${annonceId}`, { method: 'PUT' })
}

export function retirerFavori(annonceId: string): Promise<void> {
  return appeler<void>(`/favorites/${annonceId}`, { method: 'DELETE' })
}

export function listerFavoris(page = 1, taille = 20): Promise<PageResultats> {
  return appeler<PageResultats>(`/me/favorites?page=${page}&size=${taille}`)
}

export function listerRecherches(): Promise<RechercheEnregistree[]> {
  return appeler<RechercheEnregistree[]>('/me/searches')
}

export function enregistrerRecherche(
  nom: string,
  criteres: Criteres,
  frequence: Frequence,
): Promise<{ id: string }> {
  return appeler<{ id: string }>('/me/searches', {
    method: 'POST',
    body: JSON.stringify({ name: nom, criteria: criteres, frequency: frequence }),
  })
}

export function supprimerRecherche(id: string): Promise<void> {
  return appeler<void>(`/me/searches/${id}`, { method: 'DELETE' })
}

/**
 * Modifie une recherche enregistrée — le nom, les critères, la fréquence.
 *
 * C'est ainsi que l'interrupteur d'alerte bascule (« Mes recherches », 15/09/2026) : `None` ou la
 * fréquence choisie. Le serveur ne remet le compteur de nouveautés à zéro que si les critères
 * changent, pas quand on touche à la fréquence.
 */
export function modifierRecherche(
  id: string,
  nom: string,
  criteres: Criteres,
  frequence: Frequence,
): Promise<void> {
  return appeler<void>(`/me/searches/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ name: nom, criteria: criteres, frequency: frequence }),
  })
}

export function listerNouveautes(id: string, page = 1, taille = 20): Promise<PageResultats> {
  return appeler<PageResultats>(`/me/searches/${id}/news?page=${page}&size=${taille}`)
}

export function marquerRechercheVue(id: string): Promise<void> {
  return appeler<void>(`/me/searches/${id}/seen`, { method: 'POST' })
}

/**
 * Page de résultats paginée **au curseur** — L9.
 *
 * `depthCapped` distingue une fin de liste d'un plafond atteint. Sans lui, l'écran afficherait
 * « plus de résultats » dans les deux cas, et personne ne comprendrait pourquoi une annonce vue la
 * veille reste introuvable.
 */
export interface PageCurseur {
  items: Vignette[]
  nextCursor: string | null
  total: number
  depthCapped: boolean
  /**
   * La ville réellement interrogée (D44, L13 §2.5). Sans `city`, le serveur applique la ville
   * par défaut et le dit ici ; avec un code inconnu ou retiré, il se replie et le dit aussi.
   * L'écran affiche ce qu'il a obtenu, jamais ce qu'il a demandé.
   */
  appliedCityCode: string
}

/**
 * Recherche au curseur.
 *
 * **Les critères restent dans l'URL, le curseur non.** L'acquis de L3 — « une recherche
 * enregistrée est littéralement une URL » — porte sur les FILTRES : c'est eux qu'on partage, pas
 * le rang auquel on s'était arrêté. Mettre un jeton opaque dans un lien partagé le rendrait long,
 * illisible, et périmé au premier changement de filtre.
 */
export function rechercherAuCurseur(
  criteres: Criteres,
  tri: Tri,
  curseur: string | null,
  taille = 20,
): Promise<PageCurseur> {
  const params = ecrireCriteres(criteres, {
    sort: tri,
    size: String(taille),
    ...(curseur === null ? {} : { cursor: curseur }),
  })

  return appeler<PageCurseur>(`/search?${params}`)
}
